<template>
  <ion-app>
    <transition name="fade" mode="in-out">
      <ion-router-outlet></ion-router-outlet>
    </transition>
    <accountModal />
    <ion-modal :is-open="hasUpdate" backdrop-dismiss="false" mode="ios">
      <ion-header>
        <ion-toolbar>
          <ion-title>Perbarui Aplikasi</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <div class="flex flex-col gap-4 justify-between h-full">
          <div class="text-center">
            <div class="text-headline_lg_semibold">Versi terbaru tersedia</div>
            <div class="text-label_lg_regular">Untuk pengalaman terbaik, silakan perbarui aplikasi ke versi terbaru.
            </div>
          </div>
          <div class="flex justify-center items-center">
            <updateApp />
          </div>
          <div class="text-center text-label_md_regular text-gray-500">Versi terbaru mungkin berisi perbaikan bug,
            peningkatan kinerja, dan fitur baru yang akan meningkatkan pengalaman Anda.</div>
        </div>
      </ion-content>
      <ion-footer>
        <ion-toolbar>
          <div class="p-4">
            <div class="h-14 bg-primary rounded-2xl w-full flex items-center justify-center text-white"
              @click="updateNow">Perbarui Sekarang</div>
          </div>
        </ion-toolbar>
      </ion-footer>
    </ion-modal>
  </ion-app>
  <div v-if="showClose" class="w-full h-screen flex justify-center">
    <div class="rounded-full bg-black/50 z-30 px-6 py-2 text-center text-white text-md absolute bottom-12">
      Tekan sekali lagi untuk keluar
    </div>
  </div>
</template>

<script setup>
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'
import { IonApp, IonRouterOutlet, IonModal, IonContent, IonButton, IonHeader, IonToolbar, IonTitle, IonFooter } from '@ionic/vue'
import { onBeforeMount, onMounted, onBeforeUnmount, watch, ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { toastController } from '@ionic/vue'
import accountModal from './components/pages/accountModal.vue'
import { AppUpdate } from '@capawesome/capacitor-app-update'
import updateApp from '@/components/img/update_app.vue'

const router = useRouter()
const route = useRoute()
const store = useStore()

const hasUpdate = ref(false)

const isOnline = computed(() => store.state.network.isOnline)
const isRequestFailed = computed(() => store.state.network.isRequestFailed)
const isSos = computed(() => store.state.sos.isSos)
const isResponder = computed(() => store.state.home.sosResponder)

const lastBackButtonPress = ref(0)
const exitTimeout = 2000
const showClose = ref(false)
const exitPaths = ['/intro', '/home', '/login', '/register', '/profile']
const excludePaths = ['/', '/intro', '/school-code', '/login', '/register', '/offline']

const userRole = computed(() => store.state.auth.profileData?.role)
const routeName = computed(() => router.currentRoute.value.name)

const directSos = async () => {
  const isSos = store.state.sos.isSos
  if (isSos && routeName.value !== 'sos' && routeName.value !== 'sos-history' && routeName.value !== 'sos-detail') {
    router.replace('/sos')
  } else if (!isSos && routeName.value === 'sos') {
    router.replace('/home')
  }
}

onBeforeMount(async () => {
  App.addListener('backButton', () => {
    const currentTime = new Date().getTime()
    const currentPath = window.location.pathname

    if (exitPaths.includes(currentPath)) {
      if (currentTime - lastBackButtonPress.value < exitTimeout) {
        App.exitApp()
      } else {
        lastBackButtonPress.value = currentTime
        showClose.value = true
        setTimeout(() => {
          showClose.value = false
        }, 2000)
      }
    }
  })

  AppUpdate.addListener('onFlexibleUpdateStateChange', (event) => {
    updateProgress.value = event.progress
    // console.log('Update progress:', event)
  })
  store.dispatch('theme/getTheme')
})

let timeoutId = null
const normalPollingInterval = 300000
const fastPollingInterval = 60000

const currentPollingInterval = computed(() => {
  return isSos.value ? fastPollingInterval : normalPollingInterval
})

// Fungsi rekursif untuk polling
const startPolling = async () => {
  if (isResponder.value && (userRole.value === 'teacher' || (userRole.value === 'student' && isSos.value))) {
    await Promise.all([
      await store.dispatch('sos/getIsSos'),
      await store.dispatch('home/getSosResponder')
    ])
    await directSos()
    const interval = currentPollingInterval.value
    // console.log(`Polling jalan dengan interval: ${interval}ms. isSos: ${isSos.value}`)
    timeoutId = setTimeout(startPolling, interval)
  } else {
    stopPolling()
  }
}

// Fungsi untuk menghentikan polling
const stopPolling = () => {
  if (timeoutId) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
}

// watch untuk memantau perubahan route path
watch(
  () => route.path,
  async (newPath) => {
    const isExcluded = excludePaths.includes(newPath);
    // Cek apakah route path termasuk dalam excludePaths
    if (isExcluded) {
      stopPolling()
      // console.log('Polling dihentikan.')
    } else if (userRole.value === 'partner') {
      stopPolling()
    } else {
      await store.dispatch('sos/getIsSos');
      // Jika tidak, mulai polling (hanya jika belum berjalan)
      if (!timeoutId) {
        startPolling()
        // console.log('Polling jalan lagi')
      }
    }
  },
  { immediate: true }
)

// watch untuk memantau perubahan isSos dan userRole
// watch([isSos, userRole], ([newIsSos, newUserRole], [oldIsSos, oldUserRole]) => {
//   // Jika isSos berubah dari true menjadi false dan user adalah student, hentikan polling
//   if (newUserRole === 'student' && newIsSos === false && oldIsSos === true) {
//     stopPolling()
//     // console.log('Polling dihentikan karena isSos berubah menjadi false untuk student.')
//     // if (route.name === 'sos') {
//     //   router.replace('/home')
//     // }
//   }
//   // Jika role berubah, pastikan logika polling diperbarui
//   if (newUserRole !== oldUserRole) {
//     stopPolling() // Hentikan polling saat ini
//     // Jalankan logika polling baru sesuai role dan rute
//     if (!excludePaths.includes(route.path)) {
//       startPolling()
//     }
//   }
// })

// watch untuk memantau perubahan isSos, userRole, dan isResponder
watch([isSos, userRole, isResponder], ([newIsSos, newUserRole, newIsResponder], [oldIsSos, oldUserRole, oldIsResponder]) => {
  // Kondisi yang memicu perubahan interval:
  const responderChanged = newIsResponder !== oldIsResponder
  const sosChanged = newIsSos !== oldIsSos
  const roleChanged = newUserRole !== oldUserRole

  // Jika ada perubahan status penting
  if (newUserRole === 'student' && newIsSos === false && oldIsSos === true) {
    stopPolling()
  }
  if (roleChanged || responderChanged || sosChanged) {
    // Hentikan polling yang sedang berjalan
    stopPolling()
    
    // Mulai polling dengan interval baru (kecuali jika berada di excludePaths)
    if (!excludePaths.includes(route.path)) {
      startPolling()
      // console.log('Polling dimulai ulang karena perubahan isSos/isResponder/Role.')
    } else {
      // console.log('Polling dihentikan/tidak dimulai karena berada di excludePath.')
    }
  }
})

const checkUpdate = async () => {
  const info = await AppUpdate.getAppUpdateInfo({ country: 'ID' })
  // console.log('IS UPDATE = ', info.updateAvailability)
  hasUpdate.value = info.updateAvailability === 2
}

onMounted(async () => {
  const deviceInfo = await Device.getInfo()
  if (deviceInfo.platform !== 'web') {
    await checkUpdate()
  }
  await store.dispatch('network/init')
})

const updateNow = async () => {
  try {
    await AppUpdate.performImmediateUpdate()
  } catch (e) {
    console.error(e)
    await AppUpdate.openAppStore({
      appId: '6751217226'
    })
  }
}


onBeforeUnmount(() => {
  stopPolling()
})

let onlineCheckTimeout = null

watch([isOnline, isRequestFailed], async ([online, request], [oldOnline]) => {
  clearTimeout(onlineCheckTimeout)

  // Tampilkan toast saat status koneksi berubah
  if (oldOnline !== undefined && online !== oldOnline) {
    const toast = await toastController.create({
      message: online ? 'Koneksi internet kembali' : 'Koneksi internet terputus',
      duration: 2000,
      position: 'top',
      color: online ? 'success' : 'danger'
    })
    toast.present()
  }

  // Buat pindah halaman
  if (!online && request && route.name !== 'offline') {
    onlineCheckTimeout = setTimeout(() => {
      router.replace({
        name: 'offline',
        query: { redirect: route.fullPath }
      })
    }, 500)
  } else if (online && request && route.name === 'offline') {
    const redirectPath = route.query.redirect || '/home'
    router.replace(redirectPath)
    store.commit('network/SET_IS_REQUEST_FAILED', false)
  }
})

</script>

<style>
html {
  --ion-font-family: "Rubik" !important;
}

/* :root {
  --ion-safe-area-bottom: 0px;
} */

/* .ion-tab-bar, ion-tab-bar {
  padding-bottom: env(safe-area-inset-bottom);
   --padding-bottom: 0px;
} */

/* ion-header {
  padding-top: env(safe-area-inset-top);
} */

.safe-area {
  padding-top: var(--ion-safe-area-top, 0px);
}

.bottom-safe-area {
  padding-bottom: var(--ion-safe-area-bottom, 0px);
}

ion-header,
ion-toolbar {
  --border-width: 0 !important;
  --box-shadow: none !important;
}

ion-modal {
  --border-radius: 16px;
}

.no-height {
  height: 0px;
}

/*  Animasi Fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

ion-datetime {
  --background: var(--ion-background-color);
}
</style>